// FILE: src/services/streakService.js
//
// Derives a study streak from session history. Streak = number of consecutive
// calendar days, counting back from today, in which the learner completed at
// least one session. Today is included only if a session was logged today.
//
// We don't store streaks; they're derived on demand from learner_session_summary
// rows already loaded for readiness. Cheap and avoids a new column / migration.

/**
 * Derive a study streak from session rows.
 *
 * @param {Array<{ startedAt?: string }>} sessions  — newest-first, ISO timestamps
 * @param {Date} [now]                              — defaults to current time
 * @returns {{ days: number, history: boolean[] }}
 *           days    — current consecutive streak in days
 *           history — last 14 entries, oldest→newest, true if a session occurred
 *                     that calendar day (used for the streak dots in the UI)
 */
export function deriveStreak(sessions, now = new Date()) {
  if (!Array.isArray(sessions) || sessions.length === 0) {
    return { days: 0, history: makeEmptyHistory(14) };
  }

  // Bucket each session into a YYYY-MM-DD key (local time).
  const daysWith = new Set();
  for (const s of sessions) {
    const iso = s?.startedAt;
    if (!iso) continue;
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) continue;
    daysWith.add(toLocalKey(d));
  }

  // Count back from today; today only counts if a session was logged today.
  const today = startOfLocalDay(now);
  let streak = 0;
  const cursor = new Date(today);
  while (daysWith.has(toLocalKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }

  // 14-day history dots — oldest first so the UI reads left-to-right.
  const history = [];
  for (let i = 13; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    history.push(daysWith.has(toLocalKey(d)));
  }

  return { days: streak, history };
}

function toLocalKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfLocalDay(d) {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function makeEmptyHistory(n) {
  return Array.from({ length: n }, () => false);
}
